import { ContactUsService } from './../services/website/contact-us.service';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ContactPurpose, ContactPurposeLabels } from '../models/enums/contactUs';
import { ContactUsResponse } from '../models/website/contactus/contactUsResponse';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextareaModule } from 'primeng/inputtextarea';

@Component({
  selector: 'app-contact-us',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, ButtonModule, InputTextareaModule, RouterModule],
  templateUrl: './contact-us.component.html',
  styleUrls: ['./contact-us.component.scss']
})
export class ContactUsComponent implements OnInit {

  purpose: ContactPurpose | undefined;
  purposeLabel: string | undefined;
  contactForm: any;  // Make sure this is typed as FormGroup
  submitted: boolean = false;
  loading: boolean = false;
  showThankYou: boolean = false;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private contactUsService: ContactUsService,
    private messageService: MessageService,
    private router: Router
  ) { }

  faqs = [
    {
      question: 'What is GradPilots?',
      answer: `GradPilots is a higher-education guidance platform that helps students navigate their academic and career journeys — both in India and abroad. We offer personalised counselling, career clarity sessions, profile building, college admissions support, and mentorship to help students make informed decisions about their future.`
    },
    {
      question: 'Do you offer virtual counselling sessions?',
      answer: `Yes, we offer fully virtual counselling and mentorship sessions so students can access expert higher-education guidance from anywhere. Our online sessions cover career planning, college shortlisting, application support, portfolio preparation, and more.`
    },
    {
      question: 'How can GradPilots help me in pursuing higher education?',
      answer: `GradPilots supports students through every step of the higher-ed process — from identifying the right courses and fields of study to building a strong profile, preparing college applications, and planning for entrance exams or admissions requirements. Whether you're applying in India or abroad, we help you create a clear and personalised roadmap.`
    },
    {
      question: 'Which countries do you provide consulting services for?',
      answer: `We support students applying to universities in India as well as top global destinations including the USA, UK, Canada, Australia, New Zealand, Singapore, and major European countries. Our team provides region-specific guidance to help you choose programs that fit your goals.`
    },
    {
      question: 'Do you assist with university and college shortlisting and applications?',
      answer: `Absolutely. We help students shortlist colleges and universities based on their interests, academic background, and budget. Our mentors assist with course research, application planning, essay/SOP guidance, portfolio support, resume building, and submission timelines.`
    },
    {
      question: 'Is the initial consultation free?',
      answer: `Yes, we offer a free introductory consultation where students can discuss their goals, understand available pathways, and get personalised recommendations before choosing a counselling plan.`
    },
    {
      question: 'Can you help me find scholarships and financial aid?',
      answer: `Yes. We assist students in identifying relevant scholarships, grants, and financial aid opportunities — both in India and abroad. Our team helps with scholarship essays, documentation, and strategy to improve your chances of receiving financial support.`
    },
    {
      question: 'Do you help with student visa applications?',
      answer: `For students applying abroad, we provide complete visa support including documentation guidance, form filling, financial proofs, timelines, and interview preparation to ensure a smooth and successful visa process.`
    }
  ];

  activeIndex: number | null = null;

  ngOnInit(): void {
    this.setPurpose(this.route.snapshot.queryParamMap.get('purpose') ?? '');
    this.buildForm();
  }

  toggleFaq(index: number): void {
    if (this.activeIndex === index) {
      this.activeIndex = null;
    } else {
      this.activeIndex = index;
    }
  }

  setPurpose(parameter: string): void {
    switch (parameter) {
      case 'become-a-mentor':
        this.purpose = ContactPurpose.BecomeAMentor;
        break;

      case 'partnerships':
        this.purpose = ContactPurpose.Partnerships;
        break;

      case 'help-center':
        this.purpose = ContactPurpose.HelpCenter;
        break;

      case 'speak-with-adviser':
        this.purpose = ContactPurpose.SpeakToAdviser;
        break;

      case 'connect-with-mentor':
        this.purpose = ContactPurpose.ConnectWithMentor;
        break;

      case 'free-consulatation':
        this.purpose = ContactPurpose.FreeConsultation;
        break;

      default:
        this.purpose = ContactPurpose.ContactUs;
        break;
    }
    this.purposeLabel = ContactPurposeLabels[this.purpose];
  }

  // This method ensures that the form is initialized
  buildForm(): void {
    this.contactForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.pattern('[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$')]],
      phoneNumber: ['', [Validators.required, Validators.pattern('^[0-9]{10}$')]],
      message: ['', Validators.required]
    });
  }

  submit(): void {
    this.submitted = true;
    this.loading = true;
    if (this.contactForm?.invalid) {
      this.loading = false;
      return;  // Stop execution if form is invalid
    }

    this.contactUsService.submitContactUsForm({
      contactPurpose: this.purpose ?? ContactPurpose.ContactUs,
      firstName: this.contactForm.value.firstName,
      lastName: this.contactForm.value.lastName,
      email: this.contactForm.value.email,
      phoneNumber: this.contactForm.value.phoneNumber,
      message: this.contactForm.value.message
    }).subscribe({
      next: (response: ContactUsResponse) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Detail has been successfully submitted!We will contact you'
        });
        this.submitted = false;
        this.contactForm.reset();
        this.router.navigate(['/thank-you']);
      },
      error: (err: any) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Something get wrong!Please try again' });
      },
      complete: () => {
        this.loading = false;
      }
    });
  }
}
